import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Bot, ExternalLink, Send, Square } from 'lucide-react'
import { Button } from '../components/ui/button'
import { useAuthStore } from '../stores/authStore'
import { codexAPI } from '../services/codex'

const EMPTY = { connected: false, busy: false, messages: [], approvals: [] }

export default function CodexPage() {
  const { user, currentClinicId, isImpersonating } = useAuthStore()
  // Remount on identity/clinic changes so old responses cannot reappear in a new context.
  if (isImpersonating || !['super_admin', 'clinic_admin'].includes(user?.role)) {
    return <p className="p-6">Codex is available to administrators using their own Core account.</p>
  }
  return <>
    {user.role === 'super_admin' && <div className="max-w-4xl mx-auto px-6 pt-4 text-right"><Link to="/admin/codex-audit" className="text-sm underline">View Codex audit log</Link></div>}
    <CodexChat key={`${user.id || user._id}:${currentClinicId || 'all'}`} />
  </>
}

function CodexChat() {
  const [state, setState] = useState(EMPTY)
  const [draft, setDraft] = useState('')
  const [loading, setLoading] = useState(true)
  const [working, setWorking] = useState(false)
  const [error, setError] = useState('')
  const [pollError, setPollError] = useState('')
  const mounted = useRef(true)
  const mutation = useRef(false)
  const revision = useRef(0)
  const bottom = useRef(null)

  useEffect(() => {
    mounted.current = true
    let timer
    const poll = async () => {
      const version = revision.current
      try {
        if (!mutation.current) {
          const { data } = await codexAPI.status()
          if (mounted.current && version === revision.current) { setState(data); setPollError('') }
        }
      } catch (err) {
        if (mounted.current) setPollError(err.response?.data?.error?.message || 'Unable to check Codex connection.')
      } finally {
        if (mounted.current) { setLoading(false); timer = setTimeout(poll, 1500) }
      }
    }
    poll()
    return () => { mounted.current = false; revision.current++; clearTimeout(timer) }
  }, [])

  useEffect(() => { bottom.current?.scrollIntoView({ block: 'nearest' }) }, [state.messages])

  async function act(action) {
    if (mutation.current) return false
    mutation.current = true
    revision.current++
    setWorking(true)
    setError('')
    try {
      const { data } = await action()
      if (mounted.current) setState(data)
      return true
    } catch (err) {
      if (mounted.current) setError(err.response?.data?.error?.message || 'Unable to complete the Codex request.')
      return false
    } finally {
      mutation.current = false
      if (mounted.current) setWorking(false)
    }
  }

  async function submit(event) {
    event.preventDefault()
    if (!draft.trim() || working || state.busy) return
    if (await act(() => codexAPI.send(draft.trim()))) setDraft('')
  }

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><Bot className="h-6 w-6" /> Codex</h1>
          <p className="text-sm text-muted-foreground mt-1">Ask questions and work with Core using your own Codex account.</p>
        </div>
        {(state.connected || state.login) && <div className="flex gap-2">
          {state.connected && <Button variant="outline" disabled={working || state.busy} onClick={() => act(codexAPI.newChat)}>New chat</Button>}
          <Button variant="outline" disabled={working} onClick={() => act(codexAPI.disconnect)}>Disconnect</Button>
        </div>}
      </div>

      {(error || pollError || state.error) && <div role="alert" className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm">{error || pollError || state.error}</div>}
      {state.auditWarning && <p role="alert" className="text-sm text-destructive">{state.auditWarning}</p>}
      {loading ? <p role="status">Checking connection…</p> : !state.connected ? (
        <div className="rounded-xl border bg-card p-6 space-y-4">
          <h2 className="text-lg font-medium">Connect your Codex account</h2>
          <p className="text-sm text-muted-foreground">Codex uses your Core clinic access and permissions. Data retrieved to answer your questions is sent to your connected Codex account. You’ll review proposed changes before they run. Core retains prompts, responses, and tool activity in an audit log accessible to super admins.</p>
          {state.login ? <div className="space-y-3">
            <p className="text-sm">Open OpenAI’s sign-in page and enter this code:</p>
            <p className="font-mono text-xl tracking-widest select-all">{state.login.userCode}</p>
            <a className="inline-flex items-center gap-2 underline" href={state.login.verificationUrl} target="_blank" rel="noopener noreferrer">Continue to OpenAI <ExternalLink className="h-4 w-4" /></a>
            <p role="status" className="text-sm text-muted-foreground">Waiting for sign-in…</p>
          </div> : <Button disabled={working} onClick={() => act(codexAPI.connect)}>{working ? 'Connecting…' : 'Connect Codex'}</Button>}
          <p className="text-xs text-muted-foreground">Connections and chats expire after 30 minutes of inactivity or a server restart. Switching clinics starts a fresh connection.</p>
        </div>
      ) : <>
        <div className="border rounded-xl bg-card min-h-[320px] max-h-[60vh] overflow-y-auto p-4 space-y-5" role="log" aria-label="Codex conversation">
          {!state.messages.length && <div className="py-14 text-center text-muted-foreground">
            <p>What would you like to know?</p>
            <div className="flex flex-wrap justify-center gap-2 mt-4">
              {['Which clinics can I access?', 'Summarize this week’s performance', 'What is left on our express setup checklist?'].map(text => <Button key={text} variant="outline" onClick={() => setDraft(text)}>{text}</Button>)}
            </div>
          </div>}
          {state.messages.map(message => <div key={message.id} className={message.role === 'user' ? 'ml-8 rounded-lg bg-muted p-3' : 'mr-8 p-3'}>
            <p className="text-xs font-medium text-muted-foreground mb-1">{message.role === 'user' ? 'You' : 'Codex'}</p>
            <p className="whitespace-pre-wrap break-words text-sm">{message.text}</p>
          </div>)}
          {state.busy && <p role="status" className="text-sm text-muted-foreground">{state.approvals.length ? 'Waiting for your confirmation…' : 'Codex is working…'}</p>}
          <div ref={bottom} />
        </div>
        {state.approvals.map(approval => <div key={approval.id} className="border rounded-xl p-4 space-y-3">
          <h2 className="font-medium">Review proposed change: {approval.tool.replaceAll('_', ' ')}</h2>
          <pre className="text-xs whitespace-pre-wrap break-words bg-muted rounded p-3 max-h-64 overflow-auto">{JSON.stringify(approval.arguments, null, 2)}</pre>
          <div className="flex gap-2"><Button disabled={working} onClick={() => act(() => codexAPI.approve(approval.id, true))}>Approve change</Button><Button variant="outline" disabled={working} onClick={() => act(() => codexAPI.approve(approval.id, false))}>Decline</Button></div>
        </div>)}
        <form onSubmit={submit} className="space-y-2">
          <label htmlFor="codex-message" className="sr-only">Message Codex</label>
          <textarea id="codex-message" value={draft} maxLength={12000} onChange={event => setDraft(event.target.value)} placeholder="Ask Codex about your clinics…" rows={3} className="w-full resize-y rounded-lg border bg-background p-3 text-sm" disabled={working} />
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">Uses your Core permissions. Conversations and tool activity are logged for super admins.</p>
            {state.busy ? <Button type="button" variant="outline" disabled={working} onClick={() => act(codexAPI.stop)}><Square className="h-4 w-4 mr-2" />Stop</Button> : <Button type="submit" disabled={working || !draft.trim()}><Send className="h-4 w-4 mr-2" />Send</Button>}
          </div>
        </form>
      </>}
    </div>
  )
}
